import BaseModel from "@kaspernj/api-maker/build/base-model.js"
import Collection from "@kaspernj/api-maker/build/collection.js"
import modelClassRequire from "@kaspernj/api-maker/build/model-class-require.js"

const modelClassData = {
  "attributes": {
    "content": {
      "column": null,
      "name": "content",
      "selected_by_default": null,
      "translated": null
    },
    "created_at": {
      "column": {
        "default": null,
        "name": "created_at",
        "null": false,
        "type": "datetime"
      },
      "name": "created_at",
      "selected_by_default": null,
      "translated": null
    },
    "id": {
      "column": {
        "default": null,
        "name": "id",
        "null": false,
        "type": null
      },
      "name": "id",
      "selected_by_default": null,
      "translated": null
    },
    "identifier": {
      "column": {
        "default": null,
        "name": "identifier",
        "null": false,
        "type": "string"
      },
      "name": "identifier",
      "selected_by_default": null,
      "translated": null
    },
    "updated_at": {
      "column": {
        "default": null,
        "name": "updated_at",
        "null": false,
        "type": "datetime"
      },
      "name": "updated_at",
      "selected_by_default": null,
      "translated": null
    },
    "parsed_content": {
      "column": null,
      "name": "parsed_content",
      "selected_by_default": false,
      "translated": null
    }
  },
  "className": "PageContent",
  "collectionKey": "page_contents",
  "collectionName": "page_contents",
  "i18nKey": "page_content",
  "camelizedLower": "pageContent",
  "name": "PageContent",
  "nameDasherized": "page-content",
  "pluralName": "page_contents",
  "ransackable_associations": [
    {
      "className": "PageContent::Audit",
      "collectionName": "page_content_audits",
      "foreignKey": "page_content_id",
      "name": "audits",
      "macro": "has_many",
      "resource_name": "PageContentAudit",
      "through": null
    },
    {
      "className": "Content",
      "collectionName": "contents",
      "foreignKey": "resource_id",
      "name": "content_model",
      "macro": "has_one",
      "resource_name": "Content",
      "through": null
    },
    {
      "className": "Content",
      "collectionName": "contents",
      "foreignKey": "resource_id",
      "name": "contents",
      "macro": "has_many",
      "resource_name": "Content",
      "through": null
    },
    {
      "className": "PageContent::Audit",
      "collectionName": "page_content_audits",
      "foreignKey": "page_content_id",
      "name": "create_audit",
      "macro": "has_one",
      "resource_name": "PageContentAudit",
      "through": null
    }
  ],
  "ransackable_attributes": [
    {
      "name": "created_at",
      "column": {
        "default": null,
        "name": "created_at",
        "null": false,
        "type": "datetime"
      }
    },
    {
      "name": "id",
      "column": {
        "default": null,
        "name": "id",
        "null": false,
        "type": null
      }
    },
    {
      "name": "identifier",
      "column": {
        "default": null,
        "name": "identifier",
        "null": false,
        "type": "string"
      }
    },
    {
      "name": "updated_at",
      "column": {
        "default": null,
        "name": "updated_at",
        "null": false,
        "type": "datetime"
      }
    }
  ],
  "ransackable_scopes": [],
  "relationships": [
    {
      "className": "Content",
      "collectionName": "contents",
      "foreignKey": "resource_id",
      "name": "content_model",
      "macro": "has_one",
      "resource_name": "Content",
      "through": null
    },
    {
      "className": "Content",
      "collectionName": "contents",
      "foreignKey": "resource_id",
      "name": "contents",
      "macro": "has_many",
      "resource_name": "Content",
      "through": null
    }
  ],
  "paramKey": "page_content",
  "primaryKey": "id"
}

/** Frontend model for PageContent. */
class PageContent extends BaseModel {
  /** @returns {Record<string, any>} */
  static modelClassData() {
    return modelClassData
  }

  /** @returns {any} */
  content() {
    return this.readAttributeUnderscore("content")
  }

  /** @returns {boolean} */
  hasContent() {
    const value = this.content()

    return this._isPresent(value)
  }

  /** @returns {string} */
  createdAt() {
    return this.readAttributeUnderscore("created_at")
  }

  /** @returns {boolean} */
  hasCreatedAt() {
    const value = this.createdAt()

    return this._isPresent(value)
  }

  /** @returns {any} */
  id() {
    return this.readAttributeUnderscore("id")
  }

  /** @returns {boolean} */
  hasId() {
    const value = this.id()

    return this._isPresent(value)
  }

  /** @returns {string} */
  identifier() {
    return this.readAttributeUnderscore("identifier")
  }

  /** @returns {boolean} */
  hasIdentifier() {
    const value = this.identifier()

    return this._isPresent(value)
  }

  /** @returns {string} */
  updatedAt() {
    return this.readAttributeUnderscore("updated_at")
  }

  /** @returns {boolean} */
  hasUpdatedAt() {
    const value = this.updatedAt()

    return this._isPresent(value)
  }

  /** @returns {any} */
  parsedContent() {
    return this.readAttributeUnderscore("parsed_content")
  }

  /** @returns {boolean} */
  hasParsedContent() {
    const value = this.parsedContent()

    return this._isPresent(value)
  }

  /** @returns {import("./content.js").default | null} */
  contentModel() {
    return this._readHasOneReflection({reflectionName: "content_model"})
  }

  /** @returns {Promise<import("./content.js").default | null>} */
  loadContentModel() {
    if (!("id" in this)) throw new Error("Primary key method wasn't defined: id")

    const id = this.id()
    const modelClass = modelClassRequire("Content")
    const ransack = {}

    ransack["resource_id_eq"] = id

    return this._loadHasOneReflection(
      {
        reflectionName: "content_model",
        model: this,
        modelClass
      },
      {ransack}
    )
  }

  /** @returns {import("@kaspernj/api-maker/build/collection.js").default<typeof import("./content.js").default>} */
  contents() {
    if (!("id" in this)) throw new Error("No such primary key method: id")

    const modelClass = modelClassRequire("Content")

    const ransack = {}

    ransack["resource_id_eq"] = this.id()
    ransack["resource_type_eq"] = "PageContent"

    return new Collection(
      {
        reflectionName: "contents",
        model: this,
        modelName: "Content",
        modelClass
      },
      {ransack}
    )
  }

  /** @returns {Promise<Array<import("./content.js").default>>} */
  loadContents() {
    if (!("id" in this)) throw new Error("No such primary key method: id")

    const modelClass = modelClassRequire("Content")

    const ransack = {}

    ransack["resource_id_eq"] = this.id()
    ransack["resource_type_eq"] = "PageContent"

    return this._loadHasManyReflection(
      {
        reflectionName: "contents",
        model: this,
        modelClass
      },
      {ransack}
    )
  }
}

export default PageContent
